import { NewItemPrefix } from '@constants/entities/ui';
import { DomainItem } from '@constants/forms/scope-settings';
import {
  DomainsPayload,
  IDomainPayload,
  ISensitive,
  ISubDomainPayload,
  SettingsStringRowPayload,
} from '@store/services/projects/types';
import { add } from '@utils/drawers';
import { withId } from '@utils/helpers';
import { ISensitiveElement } from '@views/ProjectSettings/components/ProjectDetailsSettings/constants/sensitive-form';
import {
  mapRenamePairsForUpdate,
  normalizeAnswersToStrings,
} from '@views/ProjectSettings/components/ProjectDetailsSettings/helpers/renamePairs';

export const settingsRowToString = (
  row: string | SettingsStringRowPayload,
): string => (typeof row === 'string' ? row : row.value);

const rowIsNew = (id?: string) => !!id && id.includes(NewItemPrefix);

export const mapStringRowsForSave = (
  rows: { value: string; id?: string }[],
): SettingsStringRowPayload[] =>
  rows.map(({ id, value }) => ({
    value: value.trim(),
    isNew: rowIsNew(id),
  }));

export const prepareDomains = (domains: DomainItem[]) =>
  domains.reduce((acc, { id, name, authority, subDomains }, num) => {
    if (!name.trim() || !authority.trim()) return acc;

    const domain: IDomainPayload = {
      ...add(!id.includes(NewItemPrefix), { id }),
      name,
      authority,
      num,
      children: subDomains.reduce((acc, { id, name, authority }, num) => {
        if (!name.trim() || !authority.trim()) return acc;

        const subDomain = {
          ...add(!id.includes(NewItemPrefix), { id }),
          name,
          authority,
          num,
        };

        return [...acc, subDomain];
      }, [] as ISubDomainPayload[]),
    };

    return [...acc, domain];
  }, [] as DomainsPayload);

const previousDetailStrings = (row?: ISensitive): string[] => {
  if (!row?.children?.length) return [];
  return normalizeAnswersToStrings(row.children as unknown[]);
};

const getPreviousSensitiveItem = (
  previousSensitive: ISensitive[] | undefined,
  index: number,
  type: string,
) =>
  previousSensitive?.find(({ name }) => name === type) ??
  previousSensitive?.[index];

export const prepareSensitiveElements = (
  elements: ISensitiveElement[],
  previousSensitive?: ISensitive[],
): ISensitive[] =>
  elements.map((el, index) => {
    const prevRow = getPreviousSensitiveItem(previousSensitive, index, el.type);
    const children = mapRenamePairsForUpdate(
      el.details,
      previousDetailStrings(prevRow),
      el.detailsRenames ?? [],
    );
    return {
      name: el.type,
      isNew: rowIsNew((el as { id?: string }).id),
      children,
    };
  });

export function parseStringArray(
  array: (string | SettingsStringRowPayload)[],
): { id: string; value: string }[] {
  return array.map((item) => {
    const value = typeof item === 'string' ? item : item.value;
    return withId({ value });
  });
}

export const isAnyFieldDirty = (
  dirtyFields: any,
  ignoreKeys: string[] = [],
) => {
  const checkDirty = (obj: any) => {
    for (const key in obj) {
      if (!ignoreKeys.includes(key) && Object.hasOwn(obj, key)) {
        if (obj[key] === true) {
          return true;
        }

        if (typeof obj[key] === 'object' && obj[key] !== null) {
          if (checkDirty(obj[key])) {
            return true;
          }
        }
      }
    }

    return false;
  };

  return checkDirty(dirtyFields);
};
